/**
 * PostHog client-side analytics config for the web app (app.latolabs.io).
 *
 * Per PostHog's React guidance we mount `<PostHogProvider>` (in `__root.tsx`) and reach
 * the client via the `usePostHog()` hook rather than importing `posthog` directly. A blank
 * `VITE_POSTHOG_KEY` leaves `POSTHOG_KEY` undefined, so the provider isn't mounted and every
 * call site guards on it — the app is inert until the key is configured (mirrors the backend).
 *
 * `distinct_id` is the user's UUID (set via `identify`), matching the backend `$identify` on
 * `/users/me` and the `agent_run_*` events so web, cloud, and add-in collapse onto one person.
 * Landing (`latolabs.io`) shares the registrable domain, so the default cross-subdomain cookie
 * stitches an anonymous marketing visit to the identified app user for free.
 */

import type { PostHog, PostHogConfig } from "posthog-js";

/** Present only when analytics is configured — drives the conditional provider mount and the
 *  `usePostHog()` call guards (so a missing provider is never touched in dev). */
export const POSTHOG_KEY = (import.meta.env.VITE_POSTHOG_KEY as string | undefined) || undefined;

/** Where events are sent. In deployed envs set `VITE_POSTHOG_HOST` to our reverse proxy
 *  (`https://f.latolabs.io` → PostHog EU ingestion) so ad blockers — which catalog
 *  `*.posthog.com` but not our first-party subdomain — don't drop them. Defaults to the real
 *  EU endpoint so a missing var degrades to un-proxied-but-correct, never the wrong region. */
const HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? "https://eu.i.posthog.com";

/** Init options: the 2026-05-30 defaults snapshot (SPA `history_change` pageviews + pageleave +
 *  autocapture) plus `identified_only`. posthog-js defaults `api_host` to US, so the EU override
 *  is required. `ui_host` is PostHog's real UI domain (never the proxy): PostHog's proxy guidance
 *  requires setting both `api_host` (proxy) and `ui_host` so the toolbar and links resolve against
 *  PostHog itself. Hardcoded — it's a stable vendor domain that never varies by env. */
export const POSTHOG_OPTIONS: Partial<PostHogConfig> = {
  api_host: HOST,
  ui_host: "https://eu.posthog.com",
  defaults: "2026-05-30",
  person_profiles: "identified_only",
  // Boot cookieless + opted-out: nothing durable is stored and nothing is captured until the
  // visitor consents. ConsentManager upgrades to `localStorage+cookie` + opt_in on grant (which
  // also restores the default cross-subdomain cookie that stitches landing↔app).
  persistence: "memory",
  opt_out_capturing_by_default: true,
};

/** Start capturing (+ set the cross-subdomain stitch cookie) after the visitor grants consent.
 *  Upgrades from the cookieless `memory` boot persistence (see `POSTHOG_OPTIONS`) to
 *  `localStorage+cookie`, which restores PostHog's default cookie that stitches landing↔app. */
export function optInPostHog(posthog: PostHog): void {
  posthog.set_config({ persistence: "localStorage+cookie" });
  posthog.opt_in_capturing();
}

/** Stay cookieless and stop capturing after the visitor declines (or withdraws). Symmetric with
 *  optInPostHog: switching persistence back to `memory` clears the `localStorage+cookie` person
 *  data a prior grant set, so a withdrawal actually removes the analytics cookies (not just future
 *  capture). Our own `.latolabs.io` consent cookie re-applies the decision on the next boot. */
export function optOutPostHog(posthog: PostHog): void {
  posthog.opt_out_capturing();
  posthog.set_config({ persistence: "memory" });
}
